import type { PieceComponent, PieceGrain } from '@seddi/scene';
import {
  type Material,
  type MeshPhysicalMaterial,
  type Object3D,
  type Texture,
  BackSide,
  BufferGeometry,
  LineBasicMaterial,
  LineSegments,
  Matrix3,
  Mesh,
  MeshStandardMaterial,
  WireframeGeometry,
} from '@seddi/three';

import { assert } from '../core/assert';
import { computeVertexNormalsF32, updateIndex, updateVertexAttributeF32 } from '../core/bufferGeometry';
import { create as createStitchesTexturesMaterial } from '../materials/stitchesTexturesMaterial';
import type { GarmentGeometryGfx } from '../resources/garmentGeometryTypes';
import { clone as cloneMaterial } from '../resources/material';
import type { ResourcesContext } from '../resources/resourcesContext';
import type { StitchesTextures } from '../resources/stitchesTexturesTypes';
import { textureMapsKeys } from '../resources/textureTypes';
import type { PieceGeometryDesc } from '../scene/sceneTypes';
import {
  type PieceButtonRenderable,
  attachGarmentGeometry as attachGarmentGeometryButton,
  detachGarmentGeometry as detachGarmentGeometryButton,
  dispose as disposeButton,
} from './pieceButtonRenderable';
import {
  type PieceLogoRenderable,
  dispose as disposeLogoInternal,
  updateVisibility,
} from './pieceLogoRenderable';

export type PieceRenderable = {
  readonly buttons: PieceButtonRenderable[];
  fabricRef?: string;
  readonly geometry: BufferGeometry;
  readonly id: string;
  readonly logos: PieceLogoRenderable[];
  readonly mesh: Mesh;
  readonly parent: Object3D;
  stitchesMaterials?: MeshPhysicalMaterial[];
  stitchesTexturesRef?: string;
};

/**
 * Create piece renderable.
 * @param id - Piece ID.
 * @param parent - Parent node.
 */
export const create = (id: string, parent: Object3D): PieceRenderable => {
  // Create geometry for low-resolution.
  const geometry = new BufferGeometry();
  // Create mesh with back and front materials.
  const materials = [materialPlaceholder1.clone(), materialPlaceholder0.clone()];
  const mesh = new Mesh(geometryPlaceholder, materials);
  mesh.name = `${id}-mesh`;
  mesh.userData = { isPiece: true };
  parent.add(mesh);
  return { buttons: [], geometry, id, logos: [], mesh, parent };
};

/**
 * Destroy piece renderable.
 * @param self - Piece renderable.
 * @param resources - Resources context.
 */
export const dispose = (self: PieceRenderable, resources: ResourcesContext) => {
  const { buttons, geometry, logos, mesh, parent } = self;
  // Remove mesh from scene.
  parent.remove(mesh);
  // Detach garment geometry.
  detachGarmentGeometry(self, resources);
  // Detach stitches textures.
  detachStitchesTextures(self);
  // Dispose geometry.
  geometry.dispose();
  // Dispose buttons.
  for (const button of buttons) {
    disposeButton(button, resources);
  }
  buttons.length = 0;
  // Dispose logos.
  for (const logo of logos) {
    disposeLogoInternal(logo, resources);
  }
  logos.length = 0;
};

/**
 * Update geometry from scene.
 * @param self - Piece renderable.
 * @param data - Geometry data to update.
 */
export const updateGeometry = (self: PieceRenderable, data: Partial<PieceGeometryDesc>) => {
  const { geometry, mesh } = self;
  // Update geometry indices.
  if (data.indices) {
    updateIndex(geometry, data.indices);
  }
  // Update vertices uv coordinates.
  if (data.position2D) {
    updateVertexAttributeF32(geometry, 'uv', data.position2D, 2);
  }
  // Update vertices positions.
  if (data.position3D) {
    updateVertexAttributeF32(geometry, 'position', data.position3D, 3);
    if (data.normal) {
      updateVertexAttributeF32(geometry, 'normal', data.normal, 3);
    } else {
      computeVertexNormalsF32(geometry);
    }
  }

  // if (FlagsModule.isEnabled(FeatFlag.WIREFRAME_FABRICS)) {
  //   updateGeometryWireframe(renderable, data);
  // }

  // Update mesh.
  if (mesh.geometry !== geometry) {
    mesh.geometry = geometry;
  }
};

/**
 * Update grain.
 * @param self - Piece renderable.
 * @param grain - Grain.
 */
export const updateGrain = (() => {
  const M = new Matrix3();
  const R_1 = new Matrix3();
  const T_1 = new Matrix3();

  return (self: PieceRenderable, grain: PieceGrain): boolean => {
    const mesh = self.mesh;
    // Ensure we always work with an array of materials
    const materials: MeshPhysicalMaterial[] = Array.isArray(mesh.material)
      ? (mesh.material as MeshPhysicalMaterial[])
      : [mesh.material as MeshPhysicalMaterial];

    // Set up matrices to rotate about grain.origin.
    // Apply inverse grain translation.
    T_1.makeTranslation(-grain.origin.x, -grain.origin.y);
    // Apply inverse grain rotation.
    R_1.makeRotation(-grain.orientation);

    // Update every texture key found on each material.
    for (const material of materials) {
      if (!material.userData?.fabric) {
        continue;
      }

      // M = S x R-1 x T-1
      const { repeat } = material.map;
      // For back-side textures apply horizontal flip.
      const sx = material.side === BackSide ? -1 : 1;
      M.makeScale(sx * repeat.x, repeat.y);
      M.multiply(R_1).multiply(T_1);

      for (const key of textureMapsKeys) {
        const texture = material[key] as Texture;
        if (!texture) {
          continue;
        }

        texture.matrix.copy(M);
        texture.matrixAutoUpdate = false;
      }
    }
    return true;
  };
})();

/**
 * Recreate geometry groups.
 * @param self - Piece renderable.
 */
export const recreateGroups = (self: PieceRenderable) => {
  const mesh = self.mesh;
  const geometry = mesh.geometry;
  const material = mesh.material as Material[];
  // Group geometry several times with different materials.
  const start = 0;
  const count = geometry.index?.count ?? 0;
  geometry.clearGroups();
  for (let i = 0; i < material.length; ++i) {
    geometry.addGroup(start, count, i);
  }
};

/**
 * Recreate materials array.
 * @param self - Piece renderable.
 */
export const recreateMaterials = (self: PieceRenderable) => {
  const material = self.mesh.material as Material[];
  // keep fabric materials
  material.length = 2;
  // push logo decals
  const logos = self.logos;
  for (const logo of logos) {
    if (logo.resourceRef) {
      material.push(logo.material);
    }
  }
  // push stitch decals
  const { stitchesMaterials } = self;
  if (stitchesMaterials) {
    material.push(...stitchesMaterials);
  }
};

// ------ FABRIC ------

/**
 * Update fabric materials.
 * @param self - Piece renderable.
 * @param id - Fabric ID.
 * @param grain - Grain.
 * @param resources - Resources context.
 */
export const attachFabric = (
  self: PieceRenderable,
  id: string,
  grain: PieceGrain,
  resources: ResourcesContext,
) => {
  if (self.fabricRef) {
    detachFabric(self, resources);
  }

  // Create material instances.
  const fabric = resources.getRefFabric(id);
  const material0 = cloneMaterial(fabric.back || fabric.front);
  const material1 = cloneMaterial(fabric.front);
  material0.side = BackSide;
  const materials = self.mesh.material as unknown as Material[];
  materials[0].dispose();
  materials[1].dispose();
  materials[0] = material0;
  materials[1] = material1;
  self.fabricRef = id;

  updateGrain(self, grain);
};

/**
 * Detach fabric.
 * @param self - Piece renderable.
 * @param resources - Resources context.
 */
export const detachFabric = (self: PieceRenderable, resources: ResourcesContext) => {
  if (!self.fabricRef) {
    return;
  }
  const mesh = self.mesh;
  const materials = mesh.material as unknown as Material[];
  materials[0].dispose();
  materials[0] = materialPlaceholder0;
  materials[1].dispose();
  materials[1] = materialPlaceholder1;
  resources.retRefFabric(self.fabricRef);
  delete self.fabricRef;
};

// ------ GARMENT GEOMETRY ------

/**
 * Attach garment geometry.
 * @param self - Piece renderable.
 * @param state - Piece component.
 * @param garmentGeometry - Garment geometry resource.
 * @param resources - Resources context.
 */
export const attachGarmentGeometry = (
  self: PieceRenderable,
  state: PieceComponent,
  garmentGeometry: GarmentGeometryGfx,
  resources: ResourcesContext,
) => {
  const { buttons, id, mesh } = self;
  // Attach to mesh.
  const geometry = garmentGeometry.pieces.get(id)!;
  assert(geometry, `Garment geometry piece ${id} not found`);
  mesh.geometry = geometry;

  // TODO: Review implementation and do not do after recreate.
  // if (FlagsModule.isEnabled(FeatFlag.WIREFRAME_FABRICS)) {
  //   updateGeometryWireframe(self, garmentGeometryPiece);
  // }

  recreateGroups(self);

  // Attach to buttons.
  const buttonsState = new Map(state.buttons.map((b) => [b._id, b]));
  for (const button of buttons) {
    const buttonState = buttonsState.get(button.id);
    attachGarmentGeometryButton(button, buttonState, garmentGeometry, resources);
  }
};

/**
 * Detach garment geometry.
 * @param self - Piece renderable.
 * @param resources - Resources context.
 */
export const detachGarmentGeometry = (self: PieceRenderable, resources: ResourcesContext) => {
  const { buttons, mesh } = self;
  // Detach from mesh.
  mesh.geometry = geometryPlaceholder;
  // Detach from buttons.
  for (const button of buttons) {
    detachGarmentGeometryButton(button);
  }
};

// ------ GARMENT GEOMETRY STITCHES TEXTURES ------

/**
 * Attach stitches textures.
 * @param self - Piece renderable.
 * @param stitchesTextures - Stitches textures.
 */
export const attachStitchesTextures = (
  self: PieceRenderable,
  uvs: Float32Array,
  stitchesTextures: StitchesTextures,
) => {
  // find piece textures
  const id = self.id;
  const pieceTextures = stitchesTextures.pieces.find((p) => p.id === id);
  if (!pieceTextures) {
    return;
  }
  // create materials
  const stitchesMaterials = pieceTextures.materials.map((m) => createStitchesTexturesMaterial(m, uvs));
  self.stitchesMaterials = stitchesMaterials;
  // update groups and materials
  recreateMaterials(self);
  recreateGroups(self);
};

/**
 * Detach stitches textures.
 * @param self - Piece renderable.
 */
export const detachStitchesTextures = (self: PieceRenderable) => {
  if (!self.stitchesTexturesRef) {
    return;
  }
  // dispose materials
  for (const material of self.stitchesMaterials!) {
    material.dispose();
  }
  self.stitchesMaterials = undefined;
  self.stitchesTexturesRef = undefined;
  // update groups and materials
  recreateMaterials(self);
  recreateGroups(self);
};

// ------ LOGOS ------

/**
 * Dispose logo.
 * @param - Piece logo ID.
 */
export const disposeLogo = (self: PieceRenderable, id: string, resources: ResourcesContext) => {
  // remove logo
  const logos = self.logos;
  const index = logos.findIndex((logo) => logo.id === id);
  disposeLogoInternal(logos[index], resources);
  logos.splice(index, 1);
  // update groups and materials
  recreateMaterials(self);
  recreateGroups(self);
};

export const updateLogosVisibility = (self: PieceRenderable, visible: boolean) => {
  const pieceLogoRenderables = self.logos;
  for (const pieceLogoRenderable of pieceLogoRenderables) {
    updateVisibility(pieceLogoRenderable, visible);
  }
};

// ------ DEBUG ------

/**
 * Update geometry buffers for wireframe rendering.
 * @param self - Piece renderable.
 * @param data - Geometry data.
 */
export const updateGeometryWireframe = (() => {
  return (self: PieceRenderable, data: PieceGeometryDesc) => {
    const mesh = self.mesh;
    const wireframeMesh = mesh.getObjectByName('wireframeVersion') as Mesh;
    if (wireframeMesh) {
      //TODO: Performance - Create a new WireframeGeometry is not a cheap thing to do on every frame of an online simulation
      wireframeMesh.geometry = new WireframeGeometry(mesh.geometry);
    } else {
      const wireframeGeometry = new WireframeGeometry(mesh.geometry);
      const wireframeMesh = new LineSegments(wireframeGeometry, wireframeMaterial);
      wireframeMesh.name = 'wireframeVersion';
      // wireframeMesh.material.opacity = 5;
      wireframeMesh.material.transparent = true;
      mesh.add(wireframeMesh);
    }
  };
})();

// ------ PLACEHOLDERS ------

// Placeholders are defined in global context for now.
const geometryPlaceholder = new BufferGeometry();
const materialPlaceholder0 = new MeshStandardMaterial({ color: '#C4C4C4' });
const materialPlaceholder1 = new MeshStandardMaterial({ color: '#C4C4C4' });
materialPlaceholder1.side = BackSide;
const wireframeMaterial = new LineBasicMaterial({ color: 0x007722 });
